import { z } from 'zod';
import { CONTENT_STATUSES } from '../types/content.js';
/**
 * Validation for the unified content surface (masterclass vault, coaching
 * call replays, doer's university).
 *
 * Three editable entities — pages, courses, lessons (with media as a child
 * write inside lesson updates). Admin-created lessons are stamped
 * `manual:<uuid>` server-side so importers leave them alone on re-run.
 */
const SLUG_RE = /^[a-z0-9][a-z0-9-]{0,80}$/;
const FOUNDER_CATEGORY_CODE_RE = /^[A-Z][A-Z0-9]{1,15}$/;
const slugField = z.string().regex(SLUG_RE, 'slug must be lowercase a-z0-9-, max 81 chars');
const optionalText = (max) => z.string().trim().min(1).max(max).nullish();
const optionalUrl = z.string().url().max(2048).nullish();
/**
 * An R2 object key as minted by `POST /v1/admin/assets/*`. Not a URL — a signed
 * URL here would be persisted into the column and expire; the key is inert
 * without a signature, which is exactly why it is what gets stored.
 */
const r2Key = z.string().trim().min(1).max(1024).nullish();
const isoDatetimeNullable = z.union([z.string().datetime({ offset: true }), z.null()]);
// ── Media (child of a lesson) ────────────────────────────────────────────
export const contentMediaInputSchema = z
    .object({
    kind: z.enum(['video', 'image', 'embed']),
    provider: optionalText(64),
    provider_video_id: z.string().trim().min(1).max(256).nullish(),
    embed_html: z.string().max(20_000).nullish(),
    image_url: optionalUrl,
    thumbnail_url: optionalUrl,
    // Uploaded assets. The client round-trips the RAW object key; the API signs
    // it into the matching `*_url` on read. Both halves exist because imported
    // media still carries a LearnWorlds URL and no key.
    image_r2_key: r2Key,
    thumbnail_r2_key: r2Key,
    // Which founder categories this media variant is visible to. The server
    // maps this to a `founder_category` audience and stamps the media row's
    // `audience_id`. Empty/omitted ⇒ visible to everyone (NULL audience).
    // A lesson can carry several variants, each targeting a different set —
    // this is how the VMA badge video differs by founder industry.
    founder_categories: z
        .array(z.string().regex(FOUNDER_CATEGORY_CODE_RE, 'invalid founder category code'))
        .max(100)
        .optional(),
})
    .strict();
// ── Pages ────────────────────────────────────────────────────────────────
const pageBaseShape = {
    slug: slugField,
    title: z.string().min(1).max(500),
    hero_eyebrow: optionalText(200),
    hero_title: optionalText(500),
    hero_description: z.string().max(20_000).nullish(),
    hero_image_url: optionalUrl,
};
export const contentPageCreateSchema = z.object(pageBaseShape).strict();
export const contentPageUpdateSchema = z.object(pageBaseShape).strict().partial();
// ── Courses ──────────────────────────────────────────────────────────────
const courseBaseShape = {
    page_slug: slugField,
    slug: slugField,
    position: z.number().int().min(0).max(10_000),
    title: z.string().min(1).max(500),
    summary: z.string().max(20_000).nullish(),
    card_image_url: optionalUrl,
    card_image_r2_key: r2Key,
    status: z.enum(CONTENT_STATUSES),
    recorded_at: isoDatetimeNullable.optional(),
};
export const contentCourseCreateSchema = z
    .object({ ...courseBaseShape, status: courseBaseShape.status.optional() })
    .strict();
export const contentCourseUpdateSchema = z.object(courseBaseShape).strict().partial();
// Drag-to-reorder: the full ordered list of course ids; the server rewrites each
// course's `position` to its index in this array.
export const contentCourseReorderSchema = z
    .object({ ids: z.array(z.string().uuid()).min(1).max(1_000) })
    .strict();
// ── Lessons ──────────────────────────────────────────────────────────────
const lessonBaseShape = {
    position: z.number().int().min(0).max(10_000),
    title: z.string().min(1).max(500),
    body: z.string().max(200_000),
    byline: optionalText(200),
    recorded_at: isoDatetimeNullable.optional(),
    audience_id: z.string().uuid().nullish(),
    extras: z.record(z.unknown()),
    media: z.array(contentMediaInputSchema).max(20),
};
export const contentLessonCreateSchema = z
    .object({
    ...lessonBaseShape,
    position: lessonBaseShape.position.optional(),
    body: lessonBaseShape.body.optional(),
    extras: lessonBaseShape.extras.optional(),
    media: lessonBaseShape.media.optional(),
})
    .strict();
export const contentLessonUpdateSchema = z.object(lessonBaseShape).strict().partial();
// Drag-to-reorder within a course: the full ordered list of lesson ids. Same
// contract as the course reorder above — a partial list would leave the omitted
// lessons on stale positions.
export const contentLessonReorderSchema = z
    .object({ ids: z.array(z.string().uuid()).min(1).max(1_000) })
    .strict();
//# sourceMappingURL=content.js.map